import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Users, FileText, LogOut, LayoutDashboard, Hexagon } from 'lucide-react';
import toast from 'react-hot-toast'; // ✅ استدعاء التوست للرسائل
import useAuthStore from '../store/useAuthStore';
import { motion } from 'framer-motion';

const Layout = () => {
    // ✅ سحبنا isGuest من الستور
    const { user, logout, isGuest } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // ✅ دالة حارس الأمن: بتمنع الزائر يروح لصفحات معينة وتطلعله رسالة
    const handleNavigation = (e, path) => {
        if (isGuest && path !== '/dashboard') {
            e.preventDefault(); // 🛑 نمنع الانتقال للصفحة
            toast('This feature is locked! 🔒 Please Sign Up to access.', {
                icon: '🚀',
                style: {
                    borderRadius: '10px',
                    background: '#1e293b',
                    color: '#fff',
                },
            });
        }
    };

    // ✅ تعديل دالة الاستايل عشان تقبل حالة "مقفول" وتغير شفافية الزرار
    const getLinkStyle = (path, isLocked = false) => {
        const isActive = location.pathname.includes(path);
        return {
            color: isActive ? '#60a5fa' : '#94a3b8',
            backgroundColor: isActive ? '#1e293b' : 'transparent',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 15px',
            borderRadius: '8px',
            fontWeight: isActive ? '600' : '500',
            opacity: isLocked ? 0.6 : 1, // الزرار هيبقى باهت لو مقفول
            cursor: isLocked ? 'not-allowed' : 'pointer' // تغيير شكل الماوس
        };
    };

    return (
        <div style={{ display: 'flex', height: '100vh', direction: 'ltr' }}>

            {/* Sidebar - Dark Mode */}
            <motion.div
                initial={{ x: -250 }}
                animate={{ x: 0 }}
                style={{ width: '260px', backgroundColor: '#0f172a', color: 'white', padding: '24px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #1e293b' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '50px' }}>
                    <Hexagon size={32} color="#3b82f6" fill="#3b82f6" />
                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', letterSpacing: '1px' }}>Nova<span style={{ color: '#3b82f6' }}>SaaS</span></h2>
                </div>

                <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    {/* الداشبورد: مسموح للجميع (أو للـ admin/guest حسب اللوجيك بتاعك) */}
                    {(user?.role === 'admin' || isGuest) && (
                        <Link to="/dashboard" className="sidebar-link" style={getLinkStyle('/dashboard')}>
                            <LayoutDashboard size={20} /> Dashboard
                        </Link>
                    )}

                    {/* العملاء: مقفول للزوار */}
                    <Link
                        to="/clients"
                        className="sidebar-link"
                        style={getLinkStyle('/clients', isGuest)}
                        onClick={(e) => handleNavigation(e, '/clients')}
                    >
                        <Users size={20} /> Clients
                        {isGuest && <span style={{ marginLeft: 'auto', fontSize: '11px', backgroundColor: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Pro</span>}
                    </Link>

                    {/* الفواتير: مقفول للزوار */}
                    <Link
                        to="/invoices"
                        className="sidebar-link"
                        style={getLinkStyle('/invoices', isGuest)}
                        onClick={(e) => handleNavigation(e, '/invoices')}
                    >
                        <FileText size={20} /> Invoices
                        {isGuest && <span style={{ marginLeft: 'auto', fontSize: '11px', backgroundColor: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Pro</span>}
                    </Link>
                </nav>

                {/* User Info & Logout at bottom */}
                <div style={{ borderTop: '1px solid #1e293b', paddingTop: '20px', marginTop: 'auto' }}>
                    <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '15px' }}>Logged in as <strong style={{ color: 'white' }}>{user?.name || 'Guest'}</strong></p>
                    <button onClick={handleLogout} className="action-btn" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                        <LogOut size={18} /> {isGuest ? 'Sign Up' : 'Sign Out'}
                    </button>
                </div>
            </motion.div>

            {/* Main Content Area */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Navbar (Clean & Minimal) */}
                <header style={{ backgroundColor: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                    <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#0f172a' }}>
                        {location.pathname.includes('dashboard') && 'System Overview'}
                        {location.pathname.includes('clients') && 'Client Management'}
                        {location.pathname.includes('invoices') && 'Billing & Invoices'}
                    </h1>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', color: '#475569' }}>
                            {user?.name?.charAt(0).toUpperCase() || 'G'}
                        </div>
                    </div>
                </header>

                {/* Content Outlet */}
                <main style={{ padding: '40px', overflowY: 'auto', flex: 1 }}>
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default Layout;